/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'

import { Network } from '@root/entities/network/model'
import { Provider } from '@root/entities/provider/model'
import { nativePriceByNetwork } from '@root/features/realtime/atoms'

import { PairMigrationStatus } from '../model'
import * as PairExtraDetails from '../model/pair-extra-details'

import type { IPoolInfoExtended } from '@blade/blockchain-types'
import type { ServicesApi } from '@root/shared/api'

export const reatomPairExtraDetails = (dto: ServicesApi.PairDto) => {
  const extraDetailsAtom = atom(
    dto.extraDetails ? PairExtraDetails.fromDto(dto.extraDetails) : undefined,
  )

  //#region Computed
  const migratedFlagAtom = atom(dto.migrated === true)

  const migrationStatusAtom = atom((ctx) => {
    const extraDetails = ctx.spy(extraDetailsAtom)
    const migratedFlag = ctx.spy(migratedFlagAtom)

    if (!extraDetails) {
      return migratedFlag
        ? new PairMigrationStatus.MigratedFlag()
        : new PairMigrationStatus.None()
    }

    if (extraDetails.migration) {
      if ('migratedToPairAddress' in extraDetails.migration) {
        return new PairMigrationStatus.MigratedTo({
          migration: extraDetails.migration,
        })
      }

      return new PairMigrationStatus.MigratedFrom({
        migration: extraDetails.migration,
      })
    }

    if ('bondingCurve' in extraDetails && extraDetails.bondingCurve) {
      return new PairMigrationStatus.Bonding(extraDetails.bondingCurve)
    }

    return migratedFlag
      ? new PairMigrationStatus.MigratedFlag()
      : new PairMigrationStatus.None()
  })

  const bondingPercentageAtom = atom((ctx) => {
    const migrationStatus = ctx.spy(migrationStatusAtom)
    if (migrationStatus instanceof PairMigrationStatus.Bonding) {
      return migrationStatus.bondingPercentage
    }
    return undefined
  })

  const isCashBackModeAtom = atom((ctx) => {
    const extraDetails = ctx.spy(extraDetailsAtom)
    if (extraDetails?.provider === Provider.pumpSwap.id) {
      return extraDetails.isCashbackMode
    }
    return false
  })

  const isTokenizedAgentAtom = atom((ctx) => {
    const extraDetails = ctx.spy(extraDetailsAtom)
    // For pumpfun, cashback mode means tokenized agent
    if (extraDetails?.provider === Provider.pumpFun.id) {
      return extraDetails.isCashbackMode
    }
    return false
  })

  const bondingCurveAtom = atom((ctx) => {
    const extraDetails = ctx.spy(extraDetailsAtom)
    if (
      extraDetails?.provider !== Provider.pumpFun.id ||
      !extraDetails.bondingCurve
    ) {
      return undefined
    }
    return extraDetails.bondingCurve
  })

  const graduationMarketCapUsdAtom = atom((ctx) => {
    const migrationStatus = ctx.spy(migrationStatusAtom)
    // Migrated pools carry no bonding curve; use the exact market cap from the
    // migration tx (already USD) so the line stays after migration.
    if (migrationStatus instanceof PairMigrationStatus.MigratedFrom) {
      return migrationStatus.migration.migrationMarketCap
    }

    const bondingCurve = ctx.spy(bondingCurveAtom)
    if (!bondingCurve) {
      return undefined
    }
    if (bondingCurve.graduationQuoteCurrency === 'usdc') {
      return bondingCurve.graduationMcap
    }
    const solPriceUsd = ctx.spy(nativePriceByNetwork[Network.Solana])
    if (solPriceUsd.isZero()) {
      return undefined
    }
    return bondingCurve.graduationMcap.multipliedBy(solPriceUsd)
  })

  const graduationPriceUsdAtom = atom((ctx) => {
    const migrationStatus = ctx.spy(migrationStatusAtom)
    if (migrationStatus instanceof PairMigrationStatus.MigratedFrom) {
      return migrationStatus.migration.migrationPrice
    }

    const bondingCurve = ctx.spy(bondingCurveAtom)
    if (!bondingCurve) {
      return undefined
    }
    if (bondingCurve.graduationQuoteCurrency === 'usdc') {
      return bondingCurve.graduationPrice
    }
    const solPriceUsd = ctx.spy(nativePriceByNetwork[Network.Solana])
    if (solPriceUsd.isZero()) {
      return undefined
    }
    return bondingCurve.graduationPrice.multipliedBy(solPriceUsd)
  })
  //#endregion

  //#region Actions

  const updateFromDtoAction = action(
    (ctx, dto: ServicesApi.PairDto | IPoolInfoExtended) => {
      if (dto.extraDetails) {
        extraDetailsAtom(ctx, PairExtraDetails.fromDto(dto.extraDetails))
      }
      if (typeof dto.migrated === 'boolean') {
        migratedFlagAtom(ctx, dto.migrated)
      }
    },
  )

  //#endregion

  const model = {
    extraDetails: extraDetailsAtom,
    bondingPercentage: bondingPercentageAtom,
    migrationStatus: migrationStatusAtom,
    updateFromDto: updateFromDtoAction,
    isCashBackMode: isCashBackModeAtom,
    isTokenizedAgent: isTokenizedAgentAtom,
    graduationMarketCapUsd: graduationMarketCapUsdAtom,
    graduationPriceUsd: graduationPriceUsdAtom,
  }

  return model
}
